import React from 'react';
import MultiPlatformField from 'components/forms/MultiPlatformField';
import {
  AutoSaveCheckboxOptions,
  AutoSaveContentEditable,
  AutoSaveCurrencyInput,
  AutoSaveDateTimePicker,
  AutoSaveDropdown,
  AutoSaveFontDropdown,
  AutoSaveNumberPicker,
  AutoSaveRadio,
  AutoSaveTextArea,
  AutoSaveTextInput,
  AutoSaveToggle,
  AutoSaveYesNoRadio,
  AutoSaveKeyValuePairs
} from "components/forms/AutoSaveFormFields/index";
import BreakImagePicker from "hoc/BreakImagePicker";
import BreakCategoryEditorConnected from "components/composites/BreakCategoriesEditorConnected";
import AnswerResultDropdown from "components/forms/CustomFields/AnswerResultDropdown";
import ColorPaletteDropdownConnected from "components/composites/ColorPaletteDropdownConnected";
import AccordionMessage from "components/common/AccordionMessage";
import SavePaletteMessageConnected from "components/composites/SavePaletteMessageConnected";
import BreakStepCheckboxes from "components/forms/CustomFields/BreakStepCheckboxes";
import BreakStepDropdown from "components/forms/CustomFields/BreakStepDropdown";
import SpotTheDifferencePickerContainer from "components/forms/CustomFields/SpotTheDifferencePickerContainer";
import AssociateItemsAndResultsTable from "components/forms/CustomFields/FilteringStep/AssociateItemsAndResultsTable";
import DecisionTreeContainer from 'components/forms/CustomFields/DecisionTree/DecisionTreeContainer';
import HardcodedButton from "components/forms/HardcodedButton";
import HardcodedText from "components/forms/HardcodedText";
import StoreLocationImporterConnected from "../../composites/StoreLocationImporterConnected";
import AutoSaveColorInputConnected from "components/composites/AutoSaveColorInputConnected";
import AutoGenerateDesignImagesButton from "../../images/AutoGenerateDesignImagesButton";
import connectBreakField from "hoc/BreakField";
import debounceValueChanged from "../../../hoc/DebounceValueChanged";
import UserTagDropdown from "components/forms/CustomFields/UserTagDropdown";
import UserTagDropdownConnected from "components/composites/UserTagDropdownConnected";
import DynamicContentRulesContainer from 'components/forms/CustomFields/DynamicContentRules/DynamicContentRulesContainer';
import AutoSaveBlackOrWhiteDropdown from "../AutoSaveFormFields/AutoSaveBlackOrWhiteColorDropdown";
import AutoSaveReceiptCampaignDropdown from "../AutoSaveFormFields/AutoSaveReceiptCampaignDropdown";
import CampaignEscrowWidget from "../CustomFields/CampaignEscrowWidget";
import BreakTagEditor from "../CustomFields/BreakTagEditor";
import RelatedBreakEditor from "../../../pages/RelatedBreakEditor/RelatedBreakEditor";
import BreakSiteSiteConnected from "components/composites/BreakSiteSiteConnected";
import BreakSiteCategoryConnected from "components/composites/BreakSiteCategoryConnected"
import BreakSiteTagEditor from "../CustomFields/BreakSiteTagEditor";
import CurrentId from '../CustomFields/CurrentId';
import ResourceJsx from '../CustomFields/ResourceJsx';
import IdListEditor from "../CustomFields/IdListEditor";

// Create constant references to break field versions of auto save fields and other components
export const AutoSaveTextInputBreakField = connectBreakField(AutoSaveTextInput);
export const AutoSaveNumberPickerBreakField = connectBreakField(debounceValueChanged(AutoSaveNumberPicker));
export const AutoSaveCurrencyInputBreakField = connectBreakField(debounceValueChanged(AutoSaveCurrencyInput));
export const AutoSaveTextAreaBreakField = connectBreakField(AutoSaveTextArea);
export const AutoSaveContentEditableBreakField = connectBreakField(AutoSaveContentEditable);
export const AutoSaveFontDropdownBreakField = connectBreakField(AutoSaveFontDropdown);
export const AutoSaveBlackOrWhiteDropdownBreakField = connectBreakField(AutoSaveBlackOrWhiteDropdown);
export const AutoSaveDropdownBreakField = connectBreakField(AutoSaveDropdown);
export const AutoSaveReceiptCampaignDropdownBreakField = connectBreakField(AutoSaveReceiptCampaignDropdown);
export const AutoSaveYesNoRadioBreakField = connectBreakField(AutoSaveYesNoRadio);
export const AutoSaveRadioBreakField = connectBreakField(AutoSaveRadio);
export const AutoSaveToggleBreakField = connectBreakField(AutoSaveToggle);
export const AutoSaveCheckboxOptionsBreakField = connectBreakField(AutoSaveCheckboxOptions);
export const AutoSaveColorInputConnectedBreakField = connectBreakField(AutoSaveColorInputConnected);
export const AutoSaveDateTimePickerBreakField = connectBreakField(AutoSaveDateTimePicker);
export const BreakCategoryEditorBreakField = connectBreakField(BreakCategoryEditorConnected);
export const ColorPaletteDropdownBreakField = connectBreakField(ColorPaletteDropdownConnected);
export const StoreLocationImporterBreakField = connectBreakField(StoreLocationImporterConnected);
export const SpotTheDifferencePickerBreakField = connectBreakField(SpotTheDifferencePickerContainer);
export const AutoGenerateDesignImagesButtonBreakField = connectBreakField(AutoGenerateDesignImagesButton);
export const AssociateItemsAndResultsTableBreakField = connectBreakField(AssociateItemsAndResultsTable);
export const DecisionTreeBreakField = connectBreakField(DecisionTreeContainer);
export const UserTagDropdownBreakField = connectBreakField(UserTagDropdownConnected);
export const CampaignEscrowWidgetBreakField = connectBreakField(CampaignEscrowWidget);
export const BreakTagEditorBreakField = connectBreakField(BreakTagEditor);
export const RelatedBreakEditorBreakField = connectBreakField(RelatedBreakEditor);
export const AutoSaveKeyValuePairsField = connectBreakField(AutoSaveKeyValuePairs);
export const CurrentIdField = connectBreakField(CurrentId);
export const ResourceJsxField = connectBreakField(ResourceJsx);
export const IdListEditorBreakField = connectBreakField(IdListEditor);

export const BreakSiteSiteBreakField = connectBreakField(BreakSiteSiteConnected);
export const BreakSiteCategoryBreakField = connectBreakField(BreakSiteCategoryConnected)
export const BreakSiteTagEditorBreakField = connectBreakField(BreakSiteTagEditor);



// Convenience method to spread config props along with received props
// Used when not using the connectBreakField
const spreadFieldProps = (Target) => (props) => <Target {...props} {...props.field.props} />;

// Convenience method to create a MultiPlatformField component with a fixed fieldComponent
const multiPlatformField = (fieldComponent) =>
  (props) => <MultiPlatformField {...props} fieldComponent={fieldComponent}/>;

// Convenience method to pass the currentPlatform as platform
// Used when not using multiPlatformField, generally when a component is only editing MASTER or it doesn't matter
const currentPlatformAsPlatform = (Target) =>
  (props) => <Target {...props} platform={props.currentPlatform}/>;

/**
 * Components to be used by MultiPlatformResourceForm
 * Each component will receive the following props:
 * {
 *    currentPlatform: "DAILYBREAK" | "EMBED" | "DEMO" | "MASTER" - Main platform being edited
 *    reducerKey: String - Key used to access meta information in state
 *    sectionKeyPath: String[] - List of sections that the component is contained in
 *    resourcePath: String[] - List of properties leading to the resource the component is using
 *    fieldPath: String[] - List of properties leading the value the component is using
 *    field: String - Config object used to generate the component
 *    formProxy: ResourceFormProxy - Proxy to the form that generated the component
 *    fieldKey: String - Unique key for the component
 * }
 */
const components = {
  'text': multiPlatformField(AutoSaveTextInputBreakField),
  'textarea': multiPlatformField(AutoSaveTextAreaBreakField),
  'contenteditable': multiPlatformField(AutoSaveContentEditableBreakField),
  'number': multiPlatformField(AutoSaveNumberPickerBreakField),
  'currency': multiPlatformField(AutoSaveCurrencyInputBreakField),
  'checkbox': multiPlatformField(AutoSaveCheckboxOptionsBreakField),
  'toggle': multiPlatformField(AutoSaveToggleBreakField),
  'dropdown': multiPlatformField(AutoSaveDropdownBreakField),
  'font': multiPlatformField(AutoSaveFontDropdownBreakField),
  'blackOrWhiteDropdown': multiPlatformField(AutoSaveBlackOrWhiteDropdownBreakField),
  'receiptCampaignDropdown': multiPlatformField(AutoSaveReceiptCampaignDropdownBreakField),
  'color': multiPlatformField(AutoSaveColorInputConnectedBreakField),
  'radio': multiPlatformField(AutoSaveRadioBreakField),
  'yesnoradio': multiPlatformField(AutoSaveYesNoRadioBreakField),
  'datetime': multiPlatformField(AutoSaveDateTimePickerBreakField),
  'image': multiPlatformField(BreakImagePicker),
  'breakStepCheckboxes': multiPlatformField(BreakStepCheckboxes),
  'breakStepDropdown': multiPlatformField(BreakStepDropdown),
  'answerResultDropdown': multiPlatformField(AnswerResultDropdown),

  'keyValuePairs': multiPlatformField(AutoSaveKeyValuePairsField),
  'currentId': CurrentId,
  'resourceJsx': ResourceJsx,
  'idListEditor': multiPlatformField(IdListEditorBreakField),


  'hardcodedButton': spreadFieldProps(HardcodedButton),
  'hardcodedText': spreadFieldProps(HardcodedText),

  'decisionTree': DecisionTreeBreakField,
  'breakTagEditor': multiPlatformField(BreakTagEditorBreakField),
  'relatedBreakEditor': multiPlatformField(RelatedBreakEditorBreakField),
  'filterAssociateDropdownWithResultsTable': currentPlatformAsPlatform(AssociateItemsAndResultsTableBreakField),
  'spotTheDiffPicker': SpotTheDifferencePickerBreakField,
  'storeLocationImporter': StoreLocationImporterBreakField,
  'ColorPaletteDropdown': ColorPaletteDropdownBreakField,
  'breakCategoryEditor': currentPlatformAsPlatform(BreakCategoryEditorBreakField),
  'savePaletteMessage': SavePaletteMessageConnected,
  'autoGenerateDesignImages': AutoGenerateDesignImagesButtonBreakField,
  'userTagDropdown': currentPlatformAsPlatform(UserTagDropdownBreakField),
  'dynamicContentRules': DynamicContentRulesContainer,
  'campaignEscrowWidget': CampaignEscrowWidgetBreakField,

  'siteDropdown': currentPlatformAsPlatform(BreakSiteSiteBreakField),
  'breakSiteCategory': currentPlatformAsPlatform(BreakSiteCategoryBreakField),
  'breakSiteTagEditor': multiPlatformField(BreakSiteTagEditorBreakField),


  // Can't find usage of 'message'
  // TODO: delete after confirming not being used
  'message': AccordionMessage,
};

export default components;



// WEBPACK FOOTER //
// ./public/components/forms/Component Sets/MultiPlatformComponents.js